import type { ApiListResult } from "~/core/models/ApiListResult.interface";
import type LxcError from "~/core/models/LxcError.class";
import RestService from "~/core/services/rest.service";
import type { GroupsUser } from "~/modules/user/models/GroupsUser.type";
import type { UserData } from "~/modules/user/models/UserData.interface";
import type { UserIdentity } from "~/modules/user/models/UserIdentity.interface";
import type { UserSession } from "~/modules/user/models/UserSession.interface";
import type { FiltersSelection } from "~/types";

export class UserService extends RestService {
  protected BASE_URL = "users";

  /**
   * Retrieve current user
   */
  getCurrentUser(): Promise<UserSession> {
    return this.$apiV2.get(`${this.BASE_URL}/me`);
  }

  /**
   * Retrieve users
   * @param page
   * @param pageSize
   * @param search
   * @param sort
   * @param otherParams
   */
  async getUsers(
    page: number,
    pageSize: number,
    search: string | FiltersSelection,
    sort?: string | null,
    otherParams?: Map<string, any>,
  ): Promise<ApiListResult<UserData>> {
    const params = {
      page,
      pageSize,
      search,
      sort,
      ...(otherParams ? Object.fromEntries(otherParams) : {}),
    };

    const result: ApiListResult<UserData> = await this.$api.get(this.BASE_URL, {
      params,
    });
    if (result.data) {
      result.data = result.data.map((user: UserData) => {
        return {
          ...user,
          userGroups: user.userGroups?.map((group: GroupsUser) => {
            return {
              ...group,
              uid: group.code,
            };
          }),
        };
      });
    }
    return result;
  }

  /**
   * Set session
   * @param code
   * @param redirectUri
   */
  setSession(code: string, redirectUri: string): Promise<UserSession> {
    const params = {
      code,
      redirectUri,
    };

    return this.$api.get(`${this.BASE_URL}/session`, { params });
  }

  /**
   * Get user by id
   * @param userId
   */
  getUserById(userId: string): Promise<UserData | LxcError> {
    return this.$api.get(`${this.BASE_URL}/${userId}`);
  }

  /**
   * Update user
   * @param user
   */
  updateUser(user: UserIdentity): Promise<UserIdentity | LxcError> {
    return this.$api.put(`${this.BASE_URL}/${user.id}`, { user });
  }
}

export default new UserService();
